import React, { useEffect, useState } from 'react';
import { ScrollView, View, Platform } from 'react-native';

//components
import { styles } from './EditDriver.styels';
import Typography from 'src/components/Text/Text';
import InputField from 'src/components/input/input';
import Button from 'src/components/Button/Button';
import IconInput from 'src/components/IconInput/IconInput';
import calendarIcon from 'src/assets/calendar-3x.png';
import arrowDown from 'src/assets/arrowDown-3x.png';
import CustomDatePicker from 'src/components/CustomDatePicker/CustomDatePicker';
import RadioModal from 'src/components/RadioModal/RadioModal';
import WebDatePickerInput from 'src/components/WebDatePickerInput/WebDatePickerInput';
import TourWrapper from 'src/components/TourWrapper/TourWrapper';
import { useTourAutoStart } from 'src/features/tour/hooks/useTourAutoStart';
import { getDriverAddTour } from 'src/features/tour/tourSteps';

//Apis and Redux
import { Driver } from '@typeRoots/common/drivers';
import { emptyDriver } from './driverEmptyState';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentDriver, toggleDriverToast } from 'src/features/drivers/slice/driverSlice';
import { useCreateDriverMutation, useUpdateDriverMutation } from '../../api/driverApi'; // Make sure you have this API method

//helpers
import { formatDate, toDate } from 'src/helpers/dateHelper';
import { RootState } from 'src/store/store';
import { useRoute, useLinkTo } from '@react-navigation/native';
import { URLS } from 'src/constants/urls';
import { toggleToastState } from 'src/helpers/toastHelper';
import Toast from 'src/components/Toast/Toast';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents'; // Import GTM helper method
import { handleInputChange } from '../../helpers/driverHelpers';

const DL_CLASS_LABELS: Record<string, string> = {
  CLASS_A: 'Class A CDL',
  CLASS_B: 'Class B CDL',
  CLASS_C: 'Class C CDL',
  NON_CDL: 'Non-CDL',
};
const DL_CLASS_VALUES: Record<string, string> = {
  'Class A CDL': 'CLASS_A',
  'Class B CDL': 'CLASS_B',
  'Class C CDL': 'CLASS_C',
  'Non-CDL': 'NON_CDL',
};

const EditDriver: React.FC = () => {
  const currentDriver = useSelector((state: RootState) => state.drivers.currentDriver);
  const currentCompany = useSelector((state: RootState) => state.company.company);
  const accountType = useSelector((state: RootState) => state.UI.accountType);

  const dispatch = useDispatch();
  const linkTo = useLinkTo();
  const route = useRoute();

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [dateOfHire, setDateOfHire] = useState('2020-01-01');
  const [dateOfBirth, setDateOfBirth] = useState('2000-01-01');
  const [driverDetails, setDriverDetails] = useState<Driver>(emptyDriver);
  const [activeDateField, setActiveDateField] = useState<'hireDate' | 'birthDate' | null>(null);
  const [saveDriverErrorToast, setSaveDriverErrorToast] = useState(false);
  //radio modal state
  const [isVisible, setIsVisible] = useState(false);
  const [isDlClassVisible, setIsDlClassVisible] = useState(false);

  let isUpdateMode = false;
  let routeValue = null;

  if (route.params) {
    let { id } = route.params as { id?: number };
    if (id) {
      routeValue = route.params;
      isUpdateMode = !!routeValue;
    }
  }

  const [createDriver] = useCreateDriverMutation();
  const [updateDriver] = useUpdateDriverMutation();

  // Track Driver Details Page Landed event
  useEffect(() => {
    trackGTMEvent(
      'driver-details-page-landed',
      {
        userEvent: 'User lands on Driver Details page after filling DL number and state.',
      },
      accountType,
    );
  }, []);

  // Auto-start tour on first visit
  useTourAutoStart({
    tourKey: 'driverAdd',
    driverSteps: getDriverAddTour(),
    enabled: driverDetails !== undefined && currentDriver !== undefined,
  });

  useEffect(() => {
    if (currentDriver) {
      setDriverDetails({
        ...driverDetails,
        ...currentDriver,
      });
      setDateOfHire(
        currentDriver.dateOfHire
          ? formatDate(toDate(currentDriver.dateOfHire), 'YYYY-MM-DD')
          : '2020-01-01',
      ); // Set initial hire date
      setDateOfBirth(
        currentDriver.dateOfBirth
          ? formatDate(toDate(currentDriver.dateOfBirth), 'YYYY-MM-DD')
          : '2000-01-01',
      ); // Set initial birth date
    }
  }, [currentDriver]);

  useEffect(() => {
    dispatch(toggleDriverToast(false));
  }, []);

  //input/data change
  const handleChange = (field: keyof Driver, value: string) => {
    handleInputChange(field, value, (field, updatedValue) => {
      setDriverDetails((prevDetails) => ({
        ...prevDetails,
        [field]: updatedValue,
      }));
    });
  };

  //called when we save a driver
  const handleSaveDriver = async () => {
    try {
      // Track Driver Details Given event
      trackGTMEvent(
        'driver-details-given',
        {
          userEvent: 'User submitted driver details and navigated to Vehicle Summary screen.',
        },
        accountType,
      );

      await createDriver({
        ...driverDetails,
        dateOfBirth,
        dateOfHire,
        company: { id: currentCompany?.id as number },
      }).unwrap();

      // Clear the current driver if the request is successful
      dispatch(setCurrentDriver(emptyDriver)); // Reset the driver state
      dispatch(toggleDriverToast(true));
      linkTo(URLS.DRIVER.BYPASS);
    } catch (err) {
      setSaveDriverErrorToast(true);
      toggleToastState(setSaveDriverErrorToast);
      console.error('An error occurred while saving the driver:', err);
    }
  };
  //called when we update a driver
  const handleUpdateDriver = async () => {
    let { id } = routeValue as { id?: number };
    try {
      await updateDriver({
        id: id as number,
        updateDriverData: {
          ...driverDetails,
          dateOfBirth,
          dateOfHire,
          company: { id: currentCompany?.id as number },
        },
      }).unwrap(); // Call the mutation and wait for the result

      trackGTMEvent(
        'driver-details-given',
        {
          userEvent: 'User updated driver details and navigated to Vehicle Summary screen.',
        },
        accountType,
      );

      dispatch(setCurrentDriver(emptyDriver)); // Reset the driver state
      dispatch(toggleDriverToast(true));
      linkTo(URLS.DRIVER.BYPASS);
    } catch (err) {
      setSaveDriverErrorToast(true);
      toggleToastState(setSaveDriverErrorToast);
      console.error('An error occurred while updating the driver:', err);
    }
  };

  const handleSubmit = () => {
    if (isUpdateMode) {
      handleUpdateDriver(); // Call update function
    } else {
      handleSaveDriver();
    }
  };

  const handleBack = () => {
    // Track Driver Details Page Left event
    trackGTMEvent(
      'driver-details-page-left',
      {
        userEvent: 'User clicked Back and returned to Add Driver screen.',
      },
      accountType,
    );

    if (routeValue) {
      let { id } = routeValue as { id?: number };
      linkTo(`${URLS.DRIVER.LICENSE}/${id}`);
    } else {
      linkTo(URLS.DRIVER.LICENSE);
    }
  };
  const areDatesValid =
    !!dateOfBirth && !!dateOfHire && new Date(dateOfHire) > new Date(dateOfBirth);

  const isSaveEnabled =
    areDatesValid && driverDetails.firstName.length > 0 && driverDetails.lastName.length > 0;

  const isProducerWeb = Platform.OS === 'web' && accountType === 'producer';

  const handleDateOfHireChange = (iso: string | null) => setDateOfHire(iso ?? '');
  const handleDateOfBirthChange = (iso: string | null) => setDateOfBirth(iso ?? '');

  return (
    <ScrollView
      showsVerticalScrollIndicator={false}
      bounces={false}
      contentContainerStyle={styles.container}>
      <CustomDatePicker
        value={activeDateField === 'hireDate' ? toDate(dateOfHire) : toDate(dateOfBirth)}
        customHeader={activeDateField === 'hireDate' ? 'Date of Hire' : 'Date of Birth'}
        isOpen={isDatePickerOpen}
        onSelect={(date) => {
          if (activeDateField === 'hireDate') {
            setDateOfHire(formatDate(date, 'YYYY-MM-DD'));
          } else if (activeDateField === 'birthDate') {
            setDateOfBirth(formatDate(date, 'YYYY-MM-DD'));
          }
          setIsDatePickerOpen(false);
        }}
        onCancel={() => {
          setIsDatePickerOpen(false);
        }}
      />

      <RadioModal
        testID="edit-driver-radio-modal-maritalStatus"
        isVisible={isVisible}
        onSelectOption={(value) => handleChange('maritalStatus', value)}
        onButtonClickLeft={() => {
          setIsVisible(false);
        }}
        onClose={() => setIsVisible(false)}
        title="Please select marital status of driver"
        options={[
          { label: 'Single' },
          { label: 'Married' },
          { label: 'Divorced' },
          { label: 'Widowed' },
          { label: 'Unknown' },
        ]}
        selectedValue={driverDetails.maritalStatus}
      />
      <RadioModal
        testID="edit-driver-radio-modal-dlClass"
        isVisible={isDlClassVisible}
        onSelectOption={(label) => {
          setDriverDetails((prev) => ({
            ...prev,
            dlClass: DL_CLASS_VALUES[label] as Driver['dlClass'],
          }));
          setIsDlClassVisible(false);
        }}
        onButtonClickLeft={() => setIsDlClassVisible(false)}
        onClose={() => setIsDlClassVisible(false)}
        title="Please select CDL class"
        options={[
          { label: 'Class A CDL' },
          { label: 'Class B CDL' },
          { label: 'Class C CDL' },
          { label: 'Non-CDL' },
        ]}
        selectedValue={driverDetails.dlClass ? DL_CLASS_LABELS[driverDetails.dlClass] : ''}
      />

      <View style={styles.topContainer}>
        <Typography variant="h2">Edit more details</Typography>
        <TourWrapper tourId="driver-name-fields">
          <View>
            <InputField
              testID="edit-driver-input-f-name"
              variant="caption"
              label="First Name"
              value={driverDetails.firstName}
              placeholder="Please enter driver's first name"
              onChange={(text) => handleChange('firstName', text)}
            />
            <InputField
              testID="edit-driver-input-l-name"
              variant="caption"
              label="Last Name"
              value={driverDetails.lastName}
              placeholder="Please enter driver's last name"
              onChange={(text) => handleChange('lastName', text)}
            />
          </View>
        </TourWrapper>
        <IconInput
          testID="edit-driver-options-marital-status"
          label="Marital Status"
          value={driverDetails.maritalStatus}
          placeholder="Please select marital status of driver"
          onPress={() => {
            setIsVisible(true);
          }}
          iconSource={arrowDown}
          iconHeight={6.67}
          iconWidth={16.67}
        />
        {isProducerWeb && (
          <IconInput
            testID="edit-driver-options-dl-class"
            label="CDL Class"
            value={driverDetails.dlClass ? DL_CLASS_LABELS[driverDetails.dlClass] : ''}
            placeholder="Please select CDL class"
            onPress={() => setIsDlClassVisible(true)}
            iconSource={arrowDown}
            iconHeight={6.67}
            iconWidth={16.67}
          />
        )}
        <TourWrapper tourId="driver-experience">
          <View>
            <InputField
              testID="edit-driver-y-o-exp"
              variant="caption"
              label="Years of Experience on Equipment"
              value={driverDetails.yearsOfExperience}
              placeholder="Please enter number of years of experience"
              onChange={(text) => handleChange('yearsOfExperience', text)}
            />
            <InputField
              testID="edit-driver-y-o-exp"
              variant="caption"
              label="Months of Experience on Equipment"
              value={driverDetails.monthsOfExperience}
              placeholder="Please enter number of months of experience"
              onChange={(text) => handleChange('monthsOfExperience', text)}
            />
          </View>
        </TourWrapper>
        <TourWrapper tourId="driver-date-of-hire">
          {isProducerWeb ? (
            <WebDatePickerInput
              testID="edit-driver-date-of-hire"
              label="Date of Hire"
              valueISO={dateOfHire}
              onChangeISO={handleDateOfHireChange}
              isError={!areDatesValid}
              errorText="Hire date should be greater than birth date."
            />
          ) : (
            <IconInput
              label="Date of Hire"
              value={formatDate(toDate(dateOfHire))}
              placeholder="MM/DD/YYYY"
              // placeholder="Please select the date of hiring of driver"
              onPress={() => {
                setActiveDateField('hireDate');
                setIsDatePickerOpen(true);
              }}
              iconSource={calendarIcon}
              iconHeight={17}
              iconWidth={17}
              isError={!areDatesValid}
              errorText="Hire date should be greater than birth date."
            />
          )}
        </TourWrapper>
        {isProducerWeb ? (
          <WebDatePickerInput
            testID="edit-driver-date-of-birth"
            label="Date of Birth"
            valueISO={dateOfBirth}
            onChangeISO={handleDateOfBirthChange}
          />
        ) : (
          <IconInput
            label="Date of Birth"
            value={formatDate(toDate(dateOfBirth))}
            placeholder="MM/DD/YYYY"
            // placeholder="Please select the date of birth"
            onPress={() => {
              setActiveDateField('birthDate');
              setIsDatePickerOpen(true);
            }}
            iconSource={calendarIcon}
            iconHeight={17}
            iconWidth={17}
          />
        )}
        <TourWrapper tourId="driver-accidents">
          <InputField
            testID="edit-driver-total-accidents"
            variant="caption"
            label="Total Accidents"
            value={driverDetails.accidents}
            placeholder="Please enter the total number of accidents the driver has been involved in"
            onChange={(text) => handleChange('accidents', text)}
          />
        </TourWrapper>
        <TourWrapper tourId="driver-violations">
          <InputField
            testID="edit-driver-total-violations"
            variant="caption"
            label="Total Violations"
            value={driverDetails.violations}
            placeholder="Please enter the total number of violations by driver"
            onChange={(text) => handleChange('violations', text)}
          />
        </TourWrapper>
        <TourWrapper tourId="driver-suspensions">
          <InputField
            testID="edit-driver-total-suspensions"
            variant="caption"
            label="Total Suspensions"
            value={driverDetails.suspensions}
            placeholder="Please enter the total number of suspensions driver has faced"
            onChange={(text) => handleChange('suspensions', text)}
          />
        </TourWrapper>
        <TourWrapper tourId="driver-major-violations">
          <InputField
            testID="edit-driver-total-major-violations"
            variant="caption"
            label="Total Major Violations"
            value={driverDetails.majorViolations}
            placeholder="Please enter the total number of major violations by driver"
            onChange={(text) => handleChange('majorViolations', text)}
          />
        </TourWrapper>
        {saveDriverErrorToast && (
          <Toast
            text={`An error occurred while saving this driver's details. Please try again.`}
            type={'error'}
            animate={true}
          />
        )}
      </View>
      <View style={styles.buttonContainer}>
        <View style={styles.buttonWrapper1}>
          <Button testID="edit-driver-back-btn" type="tertiary" title="Back" onPress={handleBack} />
        </View>
        <View style={styles.buttonWrapper2}>
          <TourWrapper tourId="driver-save-btn">
            <Button
              testID="edit-driver-next-btn"
              type="primary"
              colorVariant={isSaveEnabled ? 'default' : 'disabled'}
              title="Save"
              onPress={handleSubmit}
            />
          </TourWrapper>
        </View>
      </View>
    </ScrollView>
  );
};

export default EditDriver;
